import React from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Grid,
  Heading,
  HStack,
  Icon,
  Skeleton,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiTrendingUp, FiActivity, FiBarChart2 } from 'react-icons/fi';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useSdk, SDK_LABELS } from '../contexts/SdkContext';
import { fmtSats, fmtNum, compact, compactBtc } from '../utils/format';

interface DashboardMonth {
  date: string;
  year: number;
  month: number;
  volumeSat: number;
  trades: number;
  avgTradeSizeSat: number;
}

interface DashboardData {
  sdk: string;
  totalVolumeSat: number;
  totalTrades: number;
  avgTradeSizeSat: number;
  currentMonth: {
    volumeSat: number;
    trades: number;
  };
  monthly: DashboardMonth[];
}

const BRAND = '#004dff';
const EMERALD = '#10b981';
const AMBER = '#f59e0b';
const GRID = '#eef2f7';

const DashboardPage: React.FC = () => {
  const { sdk } = useSdk();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const { data, isError, isLoading } = useQuery({
    queryKey: ['dashboard', sdk],
    queryFn: async () => {
      const response = await api.analytics.getDashboard(sdk);
      return response.data.dashboard as DashboardData;
    },
  });

  const chartData = (data?.monthly || []).map((month) => ({
    date: format(parseISO(month.date), "MMM ''yy"),
    volumeBtc: month.volumeSat / 1e8,
    trades: month.trades,
    avgTradeSize: month.avgTradeSizeSat,
  }));

  const StatCard = ({
    icon,
    iconColor,
    label,
    value,
    sub,
  }: {
    icon: React.ElementType;
    iconColor: string;
    label: string;
    value: string;
    sub: string;
  }) => (
    <Box p={4} bg={cardBg} rounded="xl" border="1px" borderColor={borderColor}>
      <HStack spacing={2} color="gray.500" fontSize="xs">
        <Icon as={icon} boxSize={5} color={iconColor} />
        <Text>{label}</Text>
      </HStack>
      <Skeleton isLoaded={!isLoading} mt={2} maxW="180px">
        <Text fontSize="2xl" fontWeight="bold">
          {value}
        </Text>
      </Skeleton>
      <Skeleton isLoaded={!isLoading} mt={1} maxW="140px">
        <Text fontSize="xs" color="gray.400">
          {sub}
        </Text>
      </Skeleton>
    </Box>
  );

  const ChartCard = ({
    title,
    children,
  }: {
    title: string;
    children: React.ReactElement;
  }) => (
    <Box p={4} bg={cardBg} rounded="xl" border="1px" borderColor={borderColor}>
      <Heading size="sm" color="gray.600" mb={3}>
        {title}
      </Heading>
      <Skeleton isLoaded={!isLoading} height="260px">
        <Box height="260px">
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        </Box>
      </Skeleton>
    </Box>
  );

  return (
    <Box>
      <Heading mb={2}>Dashboard</Heading>
      <Text color="gray.500" mb={8}>
        {SDK_LABELS[sdk]}
      </Text>

      {isError && (
        <Alert status="warning" rounded="lg" mb={8}>
          <AlertIcon />
          Failed to load stats for {SDK_LABELS[sdk]}. Please try again later.
        </Alert>
      )}

      <Grid
        templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
        gap={4}
        mb={6}
      >
        <StatCard
          icon={FiTrendingUp}
          iconColor={BRAND}
          label="Total volume"
          value={fmtSats(data?.totalVolumeSat || 0)}
          sub={`${fmtNum(data?.totalVolumeSat || 0)} sats`}
        />
        <StatCard
          icon={FiActivity}
          iconColor={EMERALD}
          label="Trades"
          value={fmtNum(data?.totalTrades || 0)}
          sub="All time"
        />
        <StatCard
          icon={FiBarChart2}
          iconColor={AMBER}
          label="Avg trade size"
          value={fmtSats(data?.avgTradeSizeSat || 0)}
          sub={`${fmtNum(data?.avgTradeSizeSat || 0)} sats`}
        />
        <StatCard
          icon={FiTrendingUp}
          iconColor="#805ad5"
          label="This month"
          value={fmtSats(data?.currentMonth.volumeSat || 0)}
          sub={`${fmtNum(data?.currentMonth.trades || 0)} trades in ${format(
            new Date(),
            'MMMM yyyy'
          )}`}
        />
      </Grid>

      <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={4}>
        <ChartCard title="Monthly volume (BTC)">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={compactBtc} width={48} tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(value: number) => [
                `${value.toLocaleString(undefined, { maximumFractionDigits: 4 })} BTC`,
                'Volume',
              ]}
            />
            <Bar
              isAnimationActive={false}
              dataKey="volumeBtc"
              fill={BRAND}
              radius={[3, 3, 0, 0]}
            />
          </BarChart>
        </ChartCard>

        <ChartCard title="Monthly trades">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={compact} width={48} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(value: number) => [fmtNum(value), 'Trades']} />
            <Bar
              isAnimationActive={false}
              dataKey="trades"
              fill={EMERALD}
              radius={[3, 3, 0, 0]}
            />
          </BarChart>
        </ChartCard>

        <ChartCard title="Avg trade size (sats)">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={compact} width={48} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(value: number) => [fmtSats(value), 'Avg trade size']} />
            <Line
              isAnimationActive={false}
              type="monotone"
              dataKey="avgTradeSize"
              stroke={AMBER}
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          </LineChart>
        </ChartCard>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
